import { DotMatrix } from '@clickguard/design-system'
import { useAuth0 } from '@auth0/auth0-react'
import { GLoading } from 'components/basic-blocks'
import { useStores, useUrlQuery } from 'hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Users } from 'services/user'
import {
	getPendingInviteCode,
	setPendingInviteCode,
	clearPendingInvite,
	isValidInviteCode
} from 'utils/invite-storage'
import { OnboardingLayout } from 'components/onboarding-layout'
import { JoinWorkspaceState } from './types'

export const JoinWorkspacePage = () => {
	const {
		isAuthenticated,
		isLoading,
		loginWithRedirect,
		logout,
		user: auth0User
	} = useAuth0()
	const { userStore, accountStore, assetStore } = useStores()
	const { user } = userStore

	const navigate = useNavigate()
	const { code } = useUrlQuery()

	const [state, setState] = useState<JoinWorkspaceState>({
		invite: null,
		wrongUser: null
	})
	const [isAccepting, setIsAccepting] = useState(false)
	const isLoadingRef = useRef(false)
	const loadedCodeRef = useRef<string | null>(null)
	const hasTriggeredAcceptRef = useRef(false)

	const acceptInvite = useCallback(
		async (inviteCode?: string): Promise<void> => {
			const codeToUse = inviteCode || code || getPendingInviteCode()
			if (!codeToUse) return

			try {
				await userStore.acceptInvite(codeToUse)
				//sleep for 1 second
				await new Promise((resolve) => setTimeout(resolve, 1000))
				clearPendingInvite()
				const accounts = await accountStore.getAllAccounts()
				const assets = await assetStore.getAllAssets()
				let account = accountStore.accounts.find(
					(a) => a.id === state.invite?.account_id
				)
				if (!account) {
					account = accounts?.find((a) => a.id === state.invite?.account_id)
				}
				let accountWithAsset = assetStore.accountsWithAssets.find(
					(a) => a.id === state.invite?.account_id
				)
				if (!accountWithAsset) {
					accountWithAsset = assets?.find(
						(a) => a.id === state.invite?.account_id
					)
				}
				if (account) {
					const assetId = accountWithAsset?.assets?.length
						? accountWithAsset.assets[0]?.id
						: 'website-demo'
					accountStore.setAccount(account)
					navigate(`/workspace/${state.invite?.account_id}/asset/${assetId}`)
				} else {
					navigate('/')
				}
			} catch (err: any) {
				console.error('Error accepting invite:', err)
				const errorMessage =
					err?.response?.data?.message ||
					err?.message ||
					'Failed to accept invitation'
				toast.error(errorMessage)
				clearPendingInvite()
				setIsAccepting(false)
				navigate('/')
				throw err
			}
		},
		[code, state.invite?.account_id, navigate]
	)

	const load = useCallback(async () => {
		if (isLoadingRef.current) return

		const inviteCode = code || getPendingInviteCode()
		if (!isValidInviteCode(inviteCode)) return

		if (loadedCodeRef.current === inviteCode) return

		isLoadingRef.current = true
		loadedCodeRef.current = inviteCode

		try {
			const _invite = await Users.getUserAccessInvite(inviteCode!)

			if (isAuthenticated && auth0User) {
				const currentEmail = auth0User.email?.toLowerCase() || ''
				const inviteEmail = _invite.user_email?.toLowerCase() || ''

				if (currentEmail && inviteEmail && currentEmail !== inviteEmail) {
					setState((prev) => ({
						...prev,
						invite: _invite,
						wrongUser: {
							inviteEmail: _invite.user_email,
							currentEmail: auth0User.email || currentEmail
						}
					}))
					isLoadingRef.current = false
					return
				}

				// Email matches — check if already has access
				if (user) {
					await accountStore.getAllAccounts()
					const hasAccess = accountStore.accounts.some(
						(a) => a.id === _invite.account_id
					)
					if (hasAccess) {
						await assetStore.getAllAssets()
						const foundAccount = accountStore.accounts.find(
							(a) => a.id === _invite.account_id
						)
						if (foundAccount) {
							const accountWithAsset = assetStore.accountsWithAssets.find(
								(a) => a.id === _invite.account_id
							)
							const assetId = accountWithAsset?.assets?.length
								? accountWithAsset.assets[0]?.id
								: 'website-demo'
							accountStore.setAccount(foundAccount)
							clearPendingInvite()
							navigate(`/workspace/${_invite.account_id}/asset/${assetId}`)
						}
					}
				}
			} else if (!isAuthenticated) {
				// Not authenticated — check if user exists to determine login vs signup
				goToLogin()
			}
			setState((prev) => ({ ...prev, invite: _invite }))
		} catch (err) {
			toast.error('Your invitation is invalid!')
			clearPendingInvite()
			navigate('/')
			loadedCodeRef.current = null
		} finally {
			isLoadingRef.current = false
		}
	}, [code, user, isAuthenticated, auth0User, navigate, loginWithRedirect])

	useEffect(() => {
		if (isValidInviteCode(code) && !getPendingInviteCode()) {
			setPendingInviteCode(code)
		}
	}, [code])

	useEffect(() => {
		if (!isLoading) {
			load()
		}
	}, [isLoading, user, isAuthenticated, load])

	const goToLogin = useCallback(async () => {
		if (isValidInviteCode(code)) {
			setPendingInviteCode(code)
			const returnTo = window.location.pathname + window.location.search
			let inviteEmail: string | undefined = undefined
			let companyName: string | undefined = undefined
			try {
				const invite = await Users.getUserAccessInvite(code!)
				if (invite?.user_email) {
					inviteEmail = invite.user_email
				}
				if (invite?.info?.owner_organization_name) {
					companyName = invite.info.owner_organization_name
				}
			} catch (err) {
				console.log('Could not fetch invite details:', err)
			}

			const loginParams: any = {
				screen_hint: 'join_workspace',
				company_name: companyName,
				appState: {
					returnTo: returnTo
				}
			}
			if (inviteEmail) {
				try {
					const userExists = await Users.checkUserExists(inviteEmail)
					loginParams.screen_hint = userExists ? 'login' : 'join_workspace'
				} catch {
					loginParams.screen_hint = 'join_workspace'
				}
				loginParams.login_hint = inviteEmail
				loginParams.authorizationParams = {
					login_hint: inviteEmail
				}
			}
			loginWithRedirect(loginParams)
		} else {
			loginWithRedirect({
				appState: {
					returnTo: window.location.pathname + window.location.search
				}
			})
		}
	}, [code, loginWithRedirect])

	useEffect(() => {
		if (
			!state.invite ||
			!user ||
			!isAuthenticated ||
			state.wrongUser ||
			hasTriggeredAcceptRef.current
		)
			return
		hasTriggeredAcceptRef.current = true
		setIsAccepting(true)
		acceptInvite().catch(() => undefined)
	}, [state.invite, state.wrongUser, user, isAuthenticated, acceptInvite])

	const handleLogoutAndContinue = () => {
		logout({
			returnTo: window.location.href
		})
	}

	if (isLoading) {
		return (
			<OnboardingLayout showStepper={false} headerVariant="sign-up">
				<GLoading />
			</OnboardingLayout>
		)
	}

	if (state.wrongUser) {
		return (
			<OnboardingLayout showStepper={false} headerVariant="sign-up">
				<div
					className="bg-white rounded-3xl p-8 max-w-md w-full text-center"
					style={{
						boxShadow:
							'0px 12px 16px 0px rgba(16,24,40,0.08), 0px 4px 6px 0px rgba(16,24,40,0.03)'
					}}
				>
					<div className="text-xl font-semibold text-t-default mb-4">
						You&apos;re signed in with a different account
					</div>
					<p className="text-t-desc-gray mb-6">
						This invite is for{' '}
						<span className="font-semibold">{state.wrongUser.inviteEmail}</span>
						, but you&apos;re currently using{' '}
						<span className="font-semibold">
							{state.wrongUser.currentEmail}
						</span>
						. To join the workspace, log out and sign back in with the invited
						email.
					</p>
					<button
						onClick={handleLogoutAndContinue}
						className="w-full px-6 py-3 bg-[#EEFF9D] border border-[#BAE100] text-[#111553] rounded-full hover:bg-[#D2FB0C] transition-colors font-medium shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)]"
					>
						Log out and continue
					</button>
				</div>
			</OnboardingLayout>
		)
	}

	if (!user) {
		return (
			<OnboardingLayout showStepper={false} headerVariant="sign-up">
				<GLoading />
			</OnboardingLayout>
		)
	}

	if (isAccepting) {
		return (
			<OnboardingLayout showStepper={false} headerVariant="sign-up">
				<div className="flex flex-col items-center gap-4">
					<DotMatrix size="lg" tone="inherit" className="text-white" />
					<span className="text-center text-white">Accepting invitation</span>
				</div>
			</OnboardingLayout>
		)
	}

	return null
}
